import {
	Check,
	ChevronLeft,
	ChevronRight,
	FolderOpen,
	Plus,
	Search,
	Tag,
	X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { Chip } from "../chip";

// ── Types ──────────────────────────────────────────────────
export interface BrowsableCategory {
	id: number;
	name: string;
	parent: number | null;
	google_id: number | null;
	childCount: number;
}

export interface CategorySearchResult {
	id: number;
	name: string;
	parent: number | null;
	google_id: number | null;
}

export interface CategorySuggestion {
	id: number;
	name: string;
	fullPath: string;
	google_id: number | null;
	score: number;
}

interface CategorySelectModalProps {
	/** Whether to show the modal */
	open: boolean;
	/** Called when the modal should close */
	onClose: () => void;
	/** Called when user selects a category — receives the category id */
	onSelect: (categoryId: number) => void | Promise<void>;
	/** Category IDs that are already added (shown as disabled) */
	excludeIds?: number[];
	/** Optional title override (default: "Select Category") */
	title?: string;
	/** If provided, shows AI suggestions from this text */
	suggestions?: CategorySuggestion[];
	/** Whether the select action is in progress */
	selecting?: boolean;
}

const inputClass =
	"px-3 py-1.5 rounded-lg border border-primary/30 bg-white text-sm text-slate-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all";

export const CategorySelectModal = ({
	open,
	onClose,
	onSelect,
	excludeIds = [],
	title = "Select Category",
	suggestions,
	selecting = false,
}: CategorySelectModalProps) => {
	// ── Search state ──
	const [searchQuery, setSearchQuery] = useState("");
	const [searchResults, setSearchResults] = useState<CategorySearchResult[]>(
		[],
	);
	const [searching, setSearching] = useState(false);
	const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	// ── Browse state ──
	const [browseCategories, setBrowseCategories] = useState<BrowsableCategory[]>(
		[],
	);
	const [browsePath, setBrowsePath] = useState<
		{ id: number | null; name: string }[]
	>([]);
	const [browseLoading, setBrowseLoading] = useState(false);
	const [activeTab, setActiveTab] = useState<
		"browse" | "search" | "suggestions"
	>(suggestions && suggestions.length > 0 ? "suggestions" : "browse");

	// Switch default tab when suggestions come in
	useEffect(() => {
		if (suggestions && suggestions.length > 0) {
			setActiveTab("suggestions");
		}
	}, [suggestions]);

	// ── Reset on close ──
	useEffect(() => {
		if (!open) {
			setSearchQuery("");
			setSearchResults([]);
			setBrowseCategories([]);
			setBrowsePath([]);
			setActiveTab(
				suggestions && suggestions.length > 0 ? "suggestions" : "browse",
			);
		}
	}, [open, suggestions]);

	// ── Fetch children for Browse tab ──
	const fetchChildren = useCallback(
		async (parentId: number | null, parentName?: string) => {
			setBrowseLoading(true);
			try {
				const query: Record<string, string> = {};
				if (parentId != null) query.parentId = String(parentId);
				const { data } = await api.categories.children.get({ query });
				setBrowseCategories((data ?? []) as unknown as BrowsableCategory[]);
				if (parentId == null) {
					setBrowsePath([]);
				} else if (parentName) {
					setBrowsePath((prev) => [
						...prev,
						{ id: parentId, name: parentName },
					]);
				}
			} catch {
				setBrowseCategories([]);
			} finally {
				setBrowseLoading(false);
			}
		},
		[],
	);

	const handleBrowseBack = async () => {
		const newPath = [...browsePath];
		newPath.pop();
		const parentEntry = newPath[newPath.length - 1];
		setBrowsePath(newPath);
		setBrowseLoading(true);
		try {
			const parentId = parentEntry?.id ?? null;
			const query: Record<string, string> = {};
			if (parentId != null) query.parentId = String(parentId);
			const { data } = await api.categories.children.get({ query });
			setBrowseCategories((data ?? []) as unknown as BrowsableCategory[]);
		} catch {
			setBrowseCategories([]);
		} finally {
			setBrowseLoading(false);
		}
	};

	const handleBrowseToPath = async (index: number) => {
		const newPath = browsePath.slice(0, index + 1);
		const parentId = newPath[newPath.length - 1]?.id ?? null;
		setBrowsePath(newPath);
		setBrowseLoading(true);
		try {
			const query: Record<string, string> = {};
			if (parentId != null) query.parentId = String(parentId);
			const { data } = await api.categories.children.get({ query });
			setBrowseCategories((data ?? []) as unknown as BrowsableCategory[]);
		} catch {
			setBrowseCategories([]);
		} finally {
			setBrowseLoading(false);
		}
	};

	// ── Search handler ──
	const handleSearch = (q: string) => {
		setSearchQuery(q);
		if (searchTimer.current) clearTimeout(searchTimer.current);
		if (!q.trim()) {
			setSearchResults([]);
			return;
		}
		searchTimer.current = setTimeout(async () => {
			setSearching(true);
			try {
				const { data } = await api.categories.search.get({ query: { q } });
				setSearchResults((data ?? []) as unknown as CategorySearchResult[]);
			} catch {
				setSearchResults([]);
			} finally {
				setSearching(false);
			}
		}, 300);
	};

	if (!open) return null;

	const isExcluded = (id: number) => excludeIds.includes(id);

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
			<div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
				{/* Header */}
				<div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
					<h3 className="text-sm font-semibold text-slate-700">{title}</h3>
					<button
						type="button"
						onClick={onClose}
						className="text-slate-400 hover:text-slate-600 transition-colors"
					>
						<X className="w-5 h-5" />
					</button>
				</div>

				{/* Tabs */}
				<div className="flex border-b border-slate-100">
					{suggestions && suggestions.length > 0 && (
						<button
							type="button"
							onClick={() => setActiveTab("suggestions")}
							className={`flex-1 px-4 py-2.5 text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
								activeTab === "suggestions"
									? "text-primary border-b-2 border-primary"
									: "text-slate-400 hover:text-slate-600"
							}`}
						>
							<Tag className="w-3.5 h-3.5" /> Suggestions
						</button>
					)}
					<button
						type="button"
						onClick={() => {
							setActiveTab("browse");
							if (browseCategories.length === 0) fetchChildren(null);
						}}
						className={`flex-1 px-4 py-2.5 text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
							activeTab === "browse"
								? "text-primary border-b-2 border-primary"
								: "text-slate-400 hover:text-slate-600"
						}`}
					>
						<FolderOpen className="w-3.5 h-3.5" /> Browse
					</button>
					<button
						type="button"
						onClick={() => setActiveTab("search")}
						className={`flex-1 px-4 py-2.5 text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
							activeTab === "search"
								? "text-primary border-b-2 border-primary"
								: "text-slate-400 hover:text-slate-600"
						}`}
					>
						<Search className="w-3.5 h-3.5" /> Search
					</button>
				</div>

				<div className="p-6 grow">
					{/* ── Suggestions tab ── */}
					{activeTab === "suggestions" &&
						suggestions &&
						suggestions.length > 0 && (
							<div className="space-y-2">
								<p className="text-xs text-slate-400 mb-3">
									Best matches based on the keyword / path content:
								</p>
								<div className="max-h-80 overflow-y-auto rounded-lg border border-slate-100 divide-y divide-slate-50">
									{suggestions.map((cat) => {
										const excluded = isExcluded(cat.id);
										return (
											<div
												key={cat.id}
												className={`flex items-center px-4 py-3 transition-colors ${
													excluded ? "bg-slate-50" : "hover:bg-slate-50/50"
												}`}
											>
												<div className="flex-1 min-w-0">
													<div className="flex items-center gap-2">
														<p
															className={`text-sm font-medium truncate ${excluded ? "text-slate-400" : "text-slate-700"}`}
														>
															{cat.name}
														</p>
														<span className="text-[10px] font-mono text-slate-400 shrink-0">
															#{cat.id}
														</span>
														<Chip
															variant={
																cat.score >= 0.7
																	? "success"
																	: cat.score >= 0.4
																		? "warning"
																		: "slate"
															}
															size="xs"
															className="font-mono shrink-0"
														>
															{cat.score.toFixed(2)}
														</Chip>
													</div>
													<p className="text-[11px] text-slate-400 truncate mt-0.5">
														{cat.fullPath}
													</p>
												</div>
												<button
													type="button"
													disabled={excluded || selecting}
													onClick={() => onSelect(cat.id)}
													className={`shrink-0 ml-3 ${
														excluded
															? "text-slate-300 cursor-not-allowed"
															: "text-primary hover:text-primary-dark transition-colors"
													}`}
													title={
														excluded ? "Already mapped" : "Select category"
													}
												>
													{excluded ? (
														<Check className="w-4 h-4" />
													) : (
														<Plus className="w-4 h-4" />
													)}
												</button>
											</div>
										);
									})}
								</div>
							</div>
						)}

					{/* ── Browse tab ── */}
					{activeTab === "browse" && (
						<>
							{/* Breadcrumb */}
							{browsePath.length > 0 && (
								<div className="flex items-center gap-1 mb-3 flex-wrap text-xs">
									<button
										type="button"
										onClick={() => {
											setBrowsePath([]);
											fetchChildren(null);
										}}
										className="text-primary hover:text-primary-dark font-medium transition-colors"
									>
										All
									</button>
									{browsePath.map((crumb, idx) => (
										<span key={crumb.id} className="flex items-center gap-1">
											<ChevronRight className="w-3 h-3 text-slate-300" />
											{idx === browsePath.length - 1 ? (
												<span className="text-slate-600 font-medium">
													{crumb.name}
												</span>
											) : (
												<button
													type="button"
													onClick={() => handleBrowseToPath(idx)}
													className="text-primary hover:text-primary-dark font-medium transition-colors"
												>
													{crumb.name}
												</button>
											)}
										</span>
									))}
								</div>
							)}

							{/* Back button */}
							{browsePath.length > 0 && (
								<button
									type="button"
									onClick={handleBrowseBack}
									className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary mb-2 transition-colors"
								>
									<ChevronLeft className="w-3.5 h-3.5" /> Back
								</button>
							)}

							{/* List */}
							<div className="max-h-128 overflow-y-auto rounded-lg border border-slate-100">
								{browseLoading ? (
									<div className="flex items-center justify-center py-8">
										<div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
									</div>
								) : browseCategories.length > 0 ? (
									<div className="divide-y divide-slate-50">
										{browseCategories.map((cat) => {
											const excluded = isExcluded(cat.id);
											return (
												<div
													key={cat.id}
													className={`flex items-center px-4 py-3 transition-colors ${
														excluded ? "bg-slate-50" : "hover:bg-slate-50/50"
													}`}
												>
													{/* Drill-down */}
													<button
														type="button"
														disabled={cat.childCount === 0}
														onClick={() =>
															cat.childCount > 0 &&
															fetchChildren(cat.id, cat.name)
														}
														className={`flex-1 text-left flex items-center gap-2 min-w-0 ${
															cat.childCount > 0
																? "cursor-pointer"
																: "cursor-default"
														}`}
													>
														{cat.childCount > 0 ? (
															<FolderOpen className="w-4 h-4 text-primary/60 shrink-0" />
														) : (
															<Tag className="w-3.5 h-3.5 text-slate-300 shrink-0" />
														)}
														<div className="min-w-0">
															<p
																className={`text-sm font-medium truncate ${excluded ? "text-slate-400" : "text-slate-700"}`}
															>
																{cat.name}
															</p>
															<p className="text-[10px] text-slate-400">
																{cat.childCount > 0 &&
																	`${cat.childCount} subcategories`}
																{cat.childCount > 0 && cat.google_id && " · "}
																{cat.google_id && `#${cat.google_id}`}
															</p>
														</div>
														{cat.childCount > 0 && (
															<ChevronRight className="w-4 h-4 text-slate-300 shrink-0 ml-auto" />
														)}
													</button>

													{/* Select button */}
													<button
														type="button"
														disabled={excluded || selecting}
														onClick={() => onSelect(cat.id)}
														className={`shrink-0 ml-3 ${
															excluded
																? "text-slate-300 cursor-not-allowed"
																: "text-primary hover:text-primary-dark transition-colors"
														}`}
														title={
															excluded ? "Already added" : "Select category"
														}
													>
														{excluded ? (
															<Check className="w-4 h-4" />
														) : (
															<Plus className="w-4 h-4" />
														)}
													</button>
												</div>
											);
										})}
									</div>
								) : (
									<p className="p-4 text-sm text-slate-400 text-center">
										No categories at this level
									</p>
								)}
							</div>
						</>
					)}

					{/* ── Search tab ── */}
					{activeTab === "search" && (
						<>
							<div className="relative">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
								<input
									type="text"
									className={`${inputClass} w-full pl-9`}
									placeholder="Search categories by name..."
									value={searchQuery}
									onChange={(e) => handleSearch(e.target.value)}
								/>
								{searching && (
									<div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
								)}
							</div>
							<div className="mt-3 max-h-80 overflow-y-auto rounded-lg border border-slate-100">
								{searchResults.length > 0 ? (
									<div className="divide-y divide-slate-50">
										{searchResults.map((cat) => {
											const excluded = isExcluded(cat.id);
											return (
												<button
													key={cat.id}
													type="button"
													disabled={excluded || selecting}
													onClick={() => onSelect(cat.id)}
													className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
														excluded
															? "bg-slate-50 text-slate-400 cursor-not-allowed"
															: "hover:bg-primary/5 text-slate-700"
													}`}
												>
													<div>
														<p className="text-sm font-medium">{cat.name}</p>
														{cat.google_id && (
															<p className="text-[10px] text-slate-400">
																Google ID: {cat.google_id}
															</p>
														)}
													</div>
													{excluded ? (
														<span className="text-[10px] bg-slate-100 text-slate-400 px-2 py-0.5 rounded-full">
															Added
														</span>
													) : (
														<Plus className="w-4 h-4 text-primary" />
													)}
												</button>
											);
										})}
									</div>
								) : searchQuery.trim() ? (
									<p className="p-4 text-sm text-slate-400 text-center">
										{searching ? "Searching..." : "No categories found"}
									</p>
								) : (
									<p className="p-4 text-sm text-slate-400 text-center">
										Start typing to search categories
									</p>
								)}
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};
